import { useEffect, useRef } from 'react';
import * as THREE from 'three';

const STREAK_COUNT = 500;
const XY_BOUNDS = 40;
const Z_BOUNDS = 20;
const PEAK_VELOCITY = 1.0;
const BASELINE_VELOCITY = 0.18;
const DECAY_RATE = 1.8;
const Z_SPEED_UNITS_PER_SEC = 150;
const STREAK_SCALE_GAIN = 32;
const STREAK_COLOR = 0x88ccff;

interface Props {
  style?: React.CSSProperties;
}

export function WarpCanvas({ style }: Props) {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const initialW = container.clientWidth || window.innerWidth;
    const initialH = container.clientHeight || window.innerHeight;

    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(100, initialW / initialH, 0.1, 200);
    camera.position.set(0, 0, 5);

    const renderer = new THREE.WebGLRenderer({ alpha: true, antialias: false });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.setSize(initialW, initialH);
    renderer.setClearColor(0x000000, 0);
    renderer.domElement.style.display = 'block';
    renderer.domElement.style.width = '100%';
    renderer.domElement.style.height = '100%';
    container.appendChild(renderer.domElement);

    const geo = new THREE.SphereGeometry(0.05, 6, 4);
    const mat = new THREE.MeshBasicMaterial({
      color: STREAK_COLOR,
      transparent: true,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      toneMapped: false,
    });
    const mesh = new THREE.InstancedMesh(geo, mat, STREAK_COUNT);
    mesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    mesh.frustumCulled = false;

    const dummy = new THREE.Object3D();
    for (let i = 0; i < STREAK_COUNT; i++) {
      dummy.position.set(
        (Math.random() - 0.5) * XY_BOUNDS,
        (Math.random() - 0.5) * XY_BOUNDS,
        (Math.random() - 0.5) * Z_BOUNDS,
      );
      dummy.updateMatrix();
      mesh.setMatrixAt(i, dummy.matrix);
    }
    mesh.instanceMatrix.needsUpdate = true;
    scene.add(mesh);

    const startTime = performance.now();
    let lastT = startTime;
    let raf = 0;

    const tempMat = new THREE.Matrix4();
    const tempPos = new THREE.Vector3();
    const tempObj = new THREE.Object3D();

    const tick = (now: number) => {
      const dt = Math.min(0.1, (now - lastT) / 1000);
      lastT = now;
      const t = (now - startTime) / 1000;

      // Burst then cruise: peak at t=0, decays toward a baseline that keeps streaks visible.
      const burstFactor = Math.exp(-t * DECAY_RATE);
      const velocity = BASELINE_VELOCITY + (PEAK_VELOCITY - BASELINE_VELOCITY) * burstFactor;
      const zStep = velocity * Z_SPEED_UNITS_PER_SEC * dt;
      const streakZScale = 1 + velocity * STREAK_SCALE_GAIN;

      for (let i = 0; i < STREAK_COUNT; i++) {
        mesh.getMatrixAt(i, tempMat);
        tempPos.setFromMatrixPosition(tempMat);

        tempPos.z += zStep;
        if (tempPos.z > Z_BOUNDS / 2) {
          tempPos.z -= Z_BOUNDS;
          tempPos.x = (Math.random() - 0.5) * XY_BOUNDS;
          tempPos.y = (Math.random() - 0.5) * XY_BOUNDS;
        }

        tempObj.position.copy(tempPos);
        tempObj.scale.set(1, 1, streakZScale);
        tempObj.updateMatrix();
        mesh.setMatrixAt(i, tempObj.matrix);
      }
      mesh.instanceMatrix.needsUpdate = true;

      renderer.render(scene, camera);
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);

    const handleResize = () => {
      const w = container.clientWidth || window.innerWidth;
      const h = container.clientHeight || window.innerHeight;
      camera.aspect = w / h;
      camera.updateProjectionMatrix();
      renderer.setSize(w, h);
    };
    window.addEventListener('resize', handleResize);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', handleResize);
      if (renderer.domElement.parentNode === container) {
        container.removeChild(renderer.domElement);
      }
      mesh.dispose();
      geo.dispose();
      mat.dispose();
      renderer.dispose();
    };
  }, []);

  return <div ref={containerRef} style={style} />;
}
